import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { ContractObservationCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputHtmlString } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { InputBoolean } from "../../../common/controls";
import { InputReferenceSet } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { AuthorComposerCreate } from "../../../authorComposer/components/create/AuthorComposerCreate";
import { AuthorComposerCreateStore } from "../../../authorComposer/store";
import { PublisherCreate } from "../../../publisher/components/create/PublisherCreate";
import { PublisherCreateStore } from "../../../publisher/store";

export type ContractObservationCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  titleSelectedValue?: { displayId: string; id: string } | null;
  onTitleChange?: (value: string) => void;
  title: string;
  titleError?: string;
  observationSelectedValue?: { displayId: string; id: string } | null;
  onObservationChange?: (value: string) => void;
  observation: string;
  observationError?: string;
  requireAllSelectedValue?: { displayId: string; id: string } | null;
  onRequireAllChange?: (value: boolean) => void;
  requireAll: boolean;
  requireAllError?: string;
  authorsSelectedValues: { displayId: string; id: string }[];
  onAuthorsSelected?: (value: { displayId: string; id: string }[]) => void;
  onAuthorsChange?: (value: string) => void;
  authorsOptions?: { displayId: string; id: string }[];
  authorsError?: string;
  publishersSelectedValues: { displayId: string; id: string }[];
  onPublishersSelected?: (value: { displayId: string; id: string }[]) => void;
  onPublishersChange?: (value: string) => void;
  publishersOptions?: { displayId: string; id: string }[];
  publishersError?: string;
  contractObservationCreateStore: ContractObservationCreateStore;
};

export const ContractObservationCreateForm: React.FC<ContractObservationCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      title = "",
      titleError,
      onTitleChange = () => {},
      observation = "",
      observationError,
      onObservationChange = () => {},
      requireAll = false,
      requireAllError,
      onRequireAllChange = () => {},
      onAuthorsSelected = () => {},
      authorsSelectedValues,
      authorsOptions = [],
      authorsError,
      onAuthorsChange = () => {},
      onPublishersSelected = () => {},
      publishersSelectedValues,
      publishersOptions = [],
      publishersError,
      onPublishersChange = () => {},
      contractObservationCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      // Custom UI-only toggle: when checked the observation has no party
      // filters (the server already treats an empty filter set as
      // "applies to every contract"). On Create the default is true; the
      // filters group is collapsed and disabled while the checkbox is on.
      // Switching it back on also wipes any filters the user typed in
      // before, so saving with the box checked never leaves stale data.
      const [includeInAll, setIncludeInAll] = React.useState(true);

      const handleIncludeInAllChange = (value: boolean) => {
        setIncludeInAll(value);
        if (value) {
          onRequireAllChange(false);
          onAuthorsSelected([]);
          onPublishersSelected([]);
        }
      };

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="ContractObservation"
              mode="create"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "title":
                    onTitleChange?.(valor);
                    break;
                  case "observation":
                    onObservationChange?.(valor);
                    break;
                  case "requireAll":
                    onRequireAllChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={contractObservationCreateStore.getCardErrors("null")}
              title={langStore.groupContractObservationLabel}
              collapsible={true}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={title}
                  error={Boolean(titleError)}
                  warning={
                    !titleError &&
                    Boolean(contractObservationCreateStore.warnings["title"])
                  }
                  helperText={
                    titleError ||
                    contractObservationCreateStore.warnings["title"]?.message
                  }
                  label={langStore.ContractObservationTitleLabel}
                  onChange={(value: string) => onTitleChange(value)}
                  description={langStore.ContractObservationTitleDescription}
                />

                <InputHtmlString
                  value={observation}
                  error={Boolean(observationError)}
                  helperText={
                    observationError ||
                    contractObservationCreateStore.warnings["observation"]
                      ?.message
                  }
                  label={langStore.ContractObservationObservationLabel}
                  onChange={(value: string) => onObservationChange(value)}
                  fragment={true}
                  description={
                    langStore.ContractObservationObservationDescription
                  }
                />
              </FormControl>
            </GroupContainer>

            <Box sx={{ px: 2, py: 1 }}>
              <InputBoolean
                value={includeInAll}
                label={langStore.contractObservationIncludeInAllLabel}
                onChange={handleIncludeInAllChange}
              />
            </Box>

            <GroupContainer
              id="filters"
              errorText={contractObservationCreateStore.getCardErrors(
                "filters"
              )}
              title={langStore.groupContractObservationFiltersLabel}
              description={langStore.groupContractObservationFiltersDescription}
              collapsible={true}
              disabled={includeInAll}
              expanded={
                includeInAll
                  ? false
                  : contractObservationCreateStore.getCardState("filters")
              }
              toggleExpand={
                includeInAll
                  ? () => {}
                  : () =>
                      contractObservationCreateStore.toggleCardState("filters")
              }
            >
              <FormControl fullWidth>
                <InputBoolean
                  value={requireAll}
                  error={Boolean(requireAllError)}
                  helperText={
                    requireAllError ||
                    contractObservationCreateStore.warnings["requireAll"]
                      ?.message
                  }
                  label={langStore.ContractObservationRequireAllLabel}
                  description={
                    langStore.ContractObservationRequireAllDescription
                  }
                  disabled={includeInAll}
                  onChange={(value: boolean) => onRequireAllChange(value)}
                />
                <InputReferenceSet
                  id="authors"
                  label={langStore.ContractObservationAuthorsLabel}
                  error={Boolean(authorsError)}
                  helperText={
                    authorsError ||
                    contractObservationCreateStore.warnings["authors"]?.message
                  }
                  selectedValues={authorsSelectedValues}
                  onChange={(value: string) => onAuthorsChange(value)}
                  onSelect={(value: { displayId: string; id: string }[]) =>
                    onAuthorsSelected(value)
                  }
                  options={authorsOptions}
                  disabled={includeInAll}
                  description={langStore.ContractObservationAuthorsDescription}
                  nullText={langStore.optionalFieldHint}
                  canCreate={rootStore.authStore.hasPermissions(
                    "authorComposer_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new AuthorComposerCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <AuthorComposerCreate
                          authorComposerCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewAuthorComposer"
                      cancelMethod="cancelNewAuthorComposer"
                    />
                  )}
                />
                <InputReferenceSet
                  id="publishers"
                  label={langStore.ContractObservationPublishersLabel}
                  error={Boolean(publishersError)}
                  helperText={
                    publishersError ||
                    contractObservationCreateStore.warnings["publishers"]
                      ?.message
                  }
                  selectedValues={publishersSelectedValues}
                  onChange={(value: string) => onPublishersChange(value)}
                  onSelect={(value: { displayId: string; id: string }[]) =>
                    onPublishersSelected(value)
                  }
                  options={publishersOptions}
                  disabled={includeInAll}
                  description={
                    langStore.ContractObservationPublishersDescription
                  }
                  nullText={langStore.optionalFieldHint}
                  canCreate={rootStore.authStore.hasPermissions(
                    "publisher_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new PublisherCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <PublisherCreate
                          publisherCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewPublisher"
                      cancelMethod="cancelNewPublisher"
                    />
                  )}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
